import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Send, 
  Bookmark, 
  BarChart3,
  ChefHat,
  Store,
  Megaphone,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const AI_AVATAR = "https://images.unsplash.com/photo-1753351052363-53ce102830eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxmcmllbmRseSUyMGJhcmlzdGElMjBwb3J0cmFpdHxlbnwwfHx8fDE3Njk4OTUxNjd8MA&ixlib=rb-4.1.0&q=85&w=200";

// Category configurations - one conversation per category
const CATEGORIES = {
  'operations': {
    icon: BarChart3,
    label: 'Operations',
    description: 'P&L analysis, cost control, profitability',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50 text-blue-700 border-blue-200',
    prompts: [
      "How's my profit margin this month?",
      "Compare this month to last month",
      "What are my biggest expenses?",
      "Why did margins change this week?"
    ]
  },
  'kitchen': {
    icon: ChefHat,
    label: 'Kitchen',
    description: 'Menu optimization, best/worst sellers, pricing',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50 text-orange-700 border-orange-200',
    prompts: [
      "Which menu items should I remove?",
      "What items are underpriced?",
      "Show my top sellers this week",
      "What's my food cost percentage?"
    ]
  },
  'front-of-house': {
    icon: Store,
    label: 'Front of House',
    description: 'Server performance, peak hours, revenue drivers',
    color: 'bg-green-500',
    lightColor: 'bg-green-50 text-green-700 border-green-200',
    prompts: [
      "Who is my best server?",
      "When should I add more staff?",
      "What are my peak hours?",
      "How can I reduce labour costs?"
    ]
  },
  'marketing': {
    icon: Megaphone,
    label: 'Marketing',
    description: 'Customer trends, promotion ideas',
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50 text-purple-700 border-purple-200',
    prompts: [
      "What brings customers back?",
      "Suggest promotions for slow days",
      "What's my average order value trend?",
      "Which day has most guests?"
    ]
  }
};

const AIChatPage = () => {
  const { currentLocation, api, user } = useAuth();
  const routeLocation = useLocation();
  
  // Get initial category from navigation state
  const initialCategory = routeLocation.state?.context || 'operations';
  
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [categoryConversations, setCategoryConversations] = useState({});
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveContent, setSaveContent] = useState('');
  const [saveEntityType, setSaveEntityType] = useState('general');
  const [entities, setEntities] = useState({ employees: [], suppliers: [], menu_items: [] });
  const [selectedEntity, setSelectedEntity] = useState('');
  const messagesEndRef = useRef(null);
  
  // Get current category config and messages
  const currentCategory = CATEGORIES[activeCategory];
  const CategoryIcon = currentCategory.icon;
  const currentConversation = categoryConversations[activeCategory];
  const messages = currentConversation?.messages || [];

  useEffect(() => {
    if (routeLocation.state?.context) {
      setActiveCategory(routeLocation.state.context);
    }
  }, [routeLocation.state]);

  useEffect(() => {
    if (currentLocation) {
      loadCategoryConversations();
      fetchEntities();
    }
  }, [currentLocation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeCategory]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadCategoryConversations = async () => {
    try {
      // Load all conversations and group by category
      const res = await api.get('/conversations', { params: { location_id: currentLocation.id } });
      const convs = res.data;
      
      // Find the latest conversation for each category
      const categoryMap = {};
      for (const conv of convs) {
        const category = conv.category || 'operations';
        if (!categoryMap[category] || conv.updated_at > categoryMap[category].updated_at) {
          categoryMap[category] = conv;
        }
      }
      setCategoryConversations(categoryMap);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  };

  const fetchEntities = async () => {
    try {
      const [empRes, supRes, menuRes] = await Promise.all([
        api.get('/employees', { params: { location_id: currentLocation.id } }),
        api.get('/suppliers', { params: { location_id: currentLocation.id } }),
        api.get('/menu-items', { params: { location_id: currentLocation.id } })
      ]);
      setEntities({
        employees: empRes.data,
        suppliers: supRes.data,
        menu_items: menuRes.data
      });
    } catch (err) {
      console.error('Error fetching entities:', err);
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  const sendMessage = async (messageText = null) => {
    const userMessage = (messageText || input).trim();
    if (!userMessage || sending) return;

    setInput('');
    setSending(true);

    // Optimistically add user message
    setCategoryConversations(prev => ({
      ...prev,
      [activeCategory]: {
        ...prev[activeCategory],
        messages: [...(prev[activeCategory]?.messages || []), { role: 'user', content: userMessage }]
      }
    }));

    try {
      const res = await api.post('/chat', {
        location_id: currentLocation.id,
        message: userMessage,
        conversation_id: currentConversation?.id,
        category: activeCategory  // Pass category for backend to track
      });

      // Update conversation with response
      setCategoryConversations(prev => ({
        ...prev,
        [activeCategory]: {
          id: res.data.conversation_id,
          category: activeCategory,
          messages: res.data.messages,
          updated_at: new Date().toISOString()
        }
      }));
      
      // Handle save suggestions
      if (res.data.save_suggestion) {
        const suggestion = res.data.save_suggestion;
        setSaveContent(suggestion.suggested_note);
        setSaveEntityType(suggestion.entity_type || 'general');
        setTimeout(() => setSaveDialogOpen(true), 500);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message');
      // Revert optimistic update
      setCategoryConversations(prev => ({
        ...prev,
        [activeCategory]: {
          ...prev[activeCategory],
          messages: prev[activeCategory]?.messages?.slice(0, -1) || []
        }
      }));
    } finally {
      setSending(false);
    }
  };

  const handleSaveInsight = async () => {
    if (!saveContent.trim()) return;

    try {
      await api.post('/notes', {
        location_id: currentLocation.id,
        content: saveContent,
        entity_type: saveEntityType,
        entity_id: selectedEntity || null,
        source: 'ai_insight'
      });
      toast.success('Insight saved');
      setSaveDialogOpen(false);
      setSaveContent('');
      setSaveEntityType('general');
      setSelectedEntity('');
    } catch (err) {
      console.error('Error saving insight:', err);
      toast.error('Failed to save insight');
    }
  };

  const openSaveDialog = (content) => {
    setSaveContent(content);
    setSaveDialogOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto h-[calc(100vh-5rem)]" data-testid="ai-chat-page">
      <div className="flex flex-col lg:flex-row gap-4 h-full">
        
        {/* Category Sidebar */}
        <div className="lg:w-56 flex-shrink-0">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI Advisor
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {Object.entries(CATEGORIES).map(([key, cat]) => {
                  const Icon = cat.icon;
                  const isActive = activeCategory === key;
                  const hasMessages = categoryConversations[key]?.messages?.length > 0;
                  
                  return (
                    <button
                      key={key}
                      onClick={() => handleCategoryChange(key)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                        isActive 
                          ? `${cat.lightColor} border` 
                          : 'hover:bg-muted/50 text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`category-${key}`}
                    >
                      <div className={`p-1.5 rounded-md ${isActive ? cat.color : 'bg-muted'}`}>
                        <Icon className={`w-4 h-4 ${isActive ? 'text-white' : ''}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${isActive ? '' : ''}`}>{cat.label}</p>
                        {hasMessages && (
                          <p className="text-xs text-muted-foreground truncate">
                            {categoryConversations[key].messages.length} messages
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chat Area */}
        <Card className="flex-1 flex flex-col min-h-0">
          {/* Header */}
          <CardHeader className="pb-3 border-b flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${currentCategory.color}`}>
                <CategoryIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-base">{currentCategory.label} Advisor</CardTitle>
                <CardDescription className="text-xs">{currentCategory.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          
          {/* Messages Area */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-4">
                {messages.length === 0 ? (
                  // Empty state with prompts
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${currentCategory.color}`}>
                      <CategoryIcon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold mb-1">{currentCategory.label} Advisor</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                      {currentCategory.description}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-md">
                      {currentCategory.prompts.map((q, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className="text-left h-auto py-2.5 px-3 justify-start text-xs"
                          onClick={() => sendMessage(q)}
                          data-testid={`prompt-${i}`}
                        >
                          {q}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Messages
                  <div className="space-y-4">
                    {messages.map((msg, i) => {
                      const displayContent = msg.role === 'assistant' 
                        ? msg.content.replace(/\[SAVE_SUGGESTION\][\s\S]*?\[\/SAVE_SUGGESTION\]/g, '').trim()
                        : msg.content;
                      
                      return (
                        <div
                          key={i}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex gap-2.5 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                            {msg.role === 'assistant' && (
                              <Avatar className="w-7 h-7 flex-shrink-0">
                                <AvatarImage src={AI_AVATAR} alt="AI" />
                                <AvatarFallback>AI</AvatarFallback>
                              </Avatar>
                            )}
                            <div className={msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}>
                              {msg.role === 'assistant' ? (
                                <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground prose-h2:text-base prose-h2:mt-3 prose-h2:mb-2 prose-h3:text-sm prose-h3:mt-2 prose-h3:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
                                  <ReactMarkdown>{displayContent}</ReactMarkdown>
                                </div>
                              ) : (
                                <p className="text-sm whitespace-pre-wrap">{displayContent}</p>
                              )}
                              {msg.role === 'assistant' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="mt-2 h-6 text-xs px-2"
                                  onClick={() => openSaveDialog(displayContent)}
                                >
                                  <Bookmark className="w-3 h-3 mr-1" />
                                  Save
                                </Button>
                              )}
                            </div>
                            {msg.role === 'user' && (
                              <Avatar className="w-7 h-7 flex-shrink-0">
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {sending && (
                      <div className="flex justify-start">
                        <div className="flex gap-2.5">
                          <Avatar className="w-7 h-7">
                            <AvatarImage src={AI_AVATAR} alt="AI" />
                            <AvatarFallback>AI</AvatarFallback>
                          </Avatar>
                          <div className="chat-bubble-ai py-3">
                            <div className="flex gap-1">
                              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Input - Always visible at bottom */}
          <div className="p-3 border-t flex-shrink-0 bg-background">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Ask about ${currentCategory.label.toLowerCase()}...`}
                disabled={sending || !currentLocation}
                className="text-sm"
                data-testid="chat-input"
              />
              <Button 
                onClick={() => sendMessage()} 
                disabled={!input.trim() || sending}
                size="sm"
                data-testid="send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Save Insight Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Insight</DialogTitle>
            <DialogDescription>
              Save this insight as a note for future reference
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Insight Content</Label>
              <Textarea
                value={saveContent}
                onChange={(e) => setSaveContent(e.target.value)}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label>Attach to Entity (Optional)</Label>
              <Select value={saveEntityType} onValueChange={setSaveEntityType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Note</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="menu_item">Menu Item</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {saveEntityType !== 'general' && (
              <div className="space-y-2">
                <Label>Select {saveEntityType.replace('_', ' ')}</Label>
                <Select value={selectedEntity} onValueChange={setSelectedEntity}>
                  <SelectTrigger>
                    <SelectValue placeholder={`Choose ${saveEntityType.replace('_', ' ')}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {saveEntityType === 'employee' && entities.employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                    {saveEntityType === 'supplier' && entities.suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                    {saveEntityType === 'menu_item' && entities.menu_items.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveInsight}>
              <Bookmark className="w-4 h-4 mr-2" />
              Save Insight
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIChatPage;
